import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    User,
    Building2,
    Mail,
    Phone,
    MapPin,
    Globe,
    Instagram,
    Facebook,
    Linkedin,
    Twitter,
    Youtube,
    Share2,
    QrCode,
    Copy,
    Download,
    Check,
    X,
    Sparkles,
    Image as ImageIcon,
    Link as LinkIcon,
    Smartphone,
    Send
} from 'lucide-react';
import { getWhatsAppUrl } from '../config/constants';

const DigitalPresentationCard = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        fullName: '',
        businessName: '',
        position: '',
        email: '',
        phone: '',
        whatsapp: '',
        address: '',
        website: '',
        bio: '',
        logo: null,
        coverImage: null,
        socialMedia: {
            instagram: '',
            facebook: '',
            linkedin: '',
            twitter: '',
            youtube: ''
        },
        theme: 'modern',
        accentColor: '#6366f1'
    });

    const [previewMode, setPreviewMode] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [copied, setCopied] = useState(false);
    const [generatedLink, setGeneratedLink] = useState('');
    const [showQrModal, setShowQrModal] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [logoPreview, setLogoPreview] = useState(null);
    const [coverPreview, setCoverPreview] = useState(null);
    const fileInputRef = useRef(null);
    const coverInputRef = useRef(null);

    const themes = [
        { id: 'modern', name: 'Moderno', gradient: 'from-primary to-secondary' },
        { id: 'minimal', name: 'Minimal', gradient: 'from-gray-700 to-gray-900' },
        { id: 'vibrant', name: 'Vibrante', gradient: 'from-purple-500 to-pink-500' },
        { id: 'professional', name: 'Profesional', gradient: 'from-blue-600 to-indigo-600' },
        { id: 'elegant', name: 'Elegante', gradient: 'from-amber-500 to-orange-500' },
        { id: 'tech', name: 'Tecnológico', gradient: 'from-cyan-500 to-blue-500' }
    ];

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('social.')) {
            const social = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                socialMedia: {
                    ...prev.socialMedia,
                    [social]: value
                }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, logo: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCoverUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, coverImage: file }));
            const reader = new FileReader();
            reader.onloadend = () => {
                setCoverPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const generateCardLink = () => {
        // In a real app, this would save to your backend and generate a unique ID
        const uniqueId = Math.random().toString(36).substring(2, 10);
        const link = `${window.location.origin}/card/${uniqueId}`;
        setGeneratedLink(link);
        setShowShareModal(true);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(generatedLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const shareViaWhatsApp = () => {
        const message = `¡Mira mi tarjeta de presentación digital!\n${generatedLink}`;
        window.open(getWhatsAppUrl(encodeURIComponent(message)), '_blank');
    };

    const shareViaEmail = () => {
        const subject = 'Mi Tarjeta de Presentación Digital';
        const body = `Te comparto mi tarjeta digital:\n${generatedLink}`;
        window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    };

    const downloadCard = () => {
        // In a real app, this would generate an image/PDF of the card
        alert('Función de descarga próximamente');
    };

    const renderStars = () => (
        <div className="absolute inset-0">
            {[...Array(30)].map((_, i) => (
                <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-secondary rounded-full"
                    style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                    }}
                    animate={{
                        opacity: [0.2, 1, 0.2],
                        scale: [1, 1.5, 1],
                    }}
                    transition={{
                        duration: Math.random() * 3 + 2,
                        repeat: Infinity,
                        delay: Math.random() * 2,
                    }}
                />
            ))}
        </div>
    );

    const renderStep1 = () => (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
        >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Información Personal</h3>

            <div className="grid md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Completo *
                    </label>
                    <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Ej: Juan Pérez"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre del Negocio *
                    </label>
                    <input
                        type="text"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Ej: Print Studio"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cargo / Posición
                    </label>
                    <input
                        type="text"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Ej: Director Creativo"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                    </label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="ejemplo@correo.com"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teléfono *
                    </label>
                    <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="+54 9 11 1234-5678"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        WhatsApp
                    </label>
                    <input
                        type="tel"
                        name="whatsapp"
                        value={formData.whatsapp}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="+54 9 11 1234-5678"
                    />
                </div>

                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dirección
                    </label>
                    <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Dirección completa"
                    />
                </div>

                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sitio Web
                    </label>
                    <input
                        type="url"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="https://tusitio.com"
                    />
                </div>

                <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Breve descripción / Bio
                    </label>
                    <textarea
                        name="bio"
                        value={formData.bio}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Cuéntanos sobre ti o tu negocio..."
                    />
                </div>
            </div>

            <div className="flex justify-end">
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setStep(2)}
                    className="px-8 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-full font-semibold hover:shadow-lg transition-all"
                >
                    Siguiente
                </motion.button>
            </div>
        </motion.div>
    );

    const renderStep2 = () => (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
        >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Imágenes y Redes Sociales</h3>

            <div className="grid md:grid-cols-2 gap-8">
                {/* Logo Upload */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Logo de tu negocio
                    </label>
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                    >
                        {logoPreview ? (
                            <div className="relative">
                                <img
                                    src={logoPreview}
                                    alt="Logo preview"
                                    className="max-h-32 mx-auto rounded-lg"
                                />
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setLogoPreview(null);
                                        setFormData(prev => ({ ...prev, logo: null }));
                                    }}
                                    className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ) : (
                            <>
                                <ImageIcon className="mx-auto text-gray-400 mb-2" size={32} />
                                <p className="text-gray-600">Haz clic para subir tu logo</p>
                                <p className="text-sm text-gray-500 mt-1">PNG, JPG (max. 2MB)</p>
                            </>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                        />
                    </div>
                </div>

                {/* Cover Image Upload */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Imagen de portada
                    </label>
                    <div
                        onClick={() => coverInputRef.current?.click()}
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-primary transition-colors"
                    >
                        {coverPreview ? (
                            <div className="relative">
                                <img
                                    src={coverPreview}
                                    alt="Cover preview"
                                    className="max-h-32 mx-auto rounded-lg object-cover"
                                />
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setCoverPreview(null);
                                        setFormData(prev => ({ ...prev, coverImage: null }));
                                    }}
                                    className="absolute top-0 right-0 bg-red-500 text-white p-1 rounded-full"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ) : (
                            <>
                                <ImageIcon className="mx-auto text-gray-400 mb-2" size={32} />
                                <p className="text-gray-600">Sube una imagen de portada</p>
                                <p className="text-sm text-gray-500 mt-1">Recomendado: 1200x400px</p>
                            </>
                        )}
                        <input
                            ref={coverInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleCoverUpload}
                            className="hidden"
                        />
                    </div>
                </div>
            </div>

            {/* Social Media Links */}
            <div className="mt-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Redes Sociales</h4>
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <Instagram size={18} className="text-pink-600 mr-2" />
                            Instagram
                        </label>
                        <input
                            type="url"
                            name="social.instagram"
                            value={formData.socialMedia.instagram}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="https://instagram.com/tu.usuario"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <Facebook size={18} className="text-blue-600 mr-2" />
                            Facebook
                        </label>
                        <input
                            type="url"
                            name="social.facebook"
                            value={formData.socialMedia.facebook}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="https://facebook.com/tu.pagina"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <Linkedin size={18} className="text-blue-700 mr-2" />
                            LinkedIn
                        </label>
                        <input
                            type="url"
                            name="social.linkedin"
                            value={formData.socialMedia.linkedin}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="https://linkedin.com/in/tu-perfil"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <Twitter size={18} className="text-blue-400 mr-2" />
                            Twitter / X
                        </label>
                        <input
                            type="url"
                            name="social.twitter"
                            value={formData.socialMedia.twitter}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="https://twitter.com/tu.usuario"
                        />
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <Youtube size={18} className="text-red-600 mr-2" />
                            YouTube
                        </label>
                        <input
                            type="url"
                            name="social.youtube"
                            value={formData.socialMedia.youtube}
                            onChange={handleInputChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            placeholder="https://youtube.com/@tu-canal"
                        />
                    </div>
                </div>
            </div>

            <div className="flex justify-between">
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setStep(1)}
                    className="px-8 py-3 bg-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-300 transition-all"
                >
                    Anterior
                </motion.button>

                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setStep(3)}
                    className="px-8 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-full font-semibold hover:shadow-lg transition-all"
                >
                    Siguiente
                </motion.button>
            </div>
        </motion.div>
    );

    const renderStep3 = () => (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
        >
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Personaliza tu Tarjeta</h3>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tema / Estilo
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {themes.map((theme) => (
                        <motion.div
                            key={theme.id}
                            whileHover={{ scale: 1.05 }}
                            onClick={() => setFormData(prev => ({ ...prev, theme: theme.id }))}
                            className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${formData.theme === theme.id
                                ? 'border-primary shadow-lg'
                                : 'border-gray-200 hover:border-primary/50'
                                }`}
                        >
                            <div className={`h-12 rounded-lg bg-gradient-to-r ${theme.gradient} mb-2`} />
                            <p className="text-sm font-medium text-gray-700 text-center">{theme.name}</p>
                        </motion.div>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    Color de Acento
                </label>
                <input
                    type="color"
                    value={formData.accentColor}
                    onChange={(e) => setFormData(prev => ({ ...prev, accentColor: e.target.value }))}
                    className="w-full h-12 rounded-lg cursor-pointer"
                />
            </div>

            <div className="flex justify-between">
                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setStep(2)}
                    className="px-8 py-3 bg-gray-200 text-gray-700 rounded-full font-semibold hover:bg-gray-300 transition-all"
                >
                    Anterior
                </motion.button>

                <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setPreviewMode(true)}
                    className="px-8 py-3 bg-gradient-to-r from-primary to-secondary text-white rounded-full font-semibold hover:shadow-lg transition-all"
                >
                    Ver Previsualización
                </motion.button>
            </div>
        </motion.div>
    );

    const renderPreview = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setPreviewMode(false)}
        >
            <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-900">Vista Previa</h3>
                    <button
                        onClick={() => setPreviewMode(false)}
                        className="p-2 hover:bg-gray-100 rounded-full"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-4">
                    {/* Card Preview */}
                    <div className={`rounded-xl overflow-hidden shadow-xl bg-gradient-to-br ${themes.find(t => t.id === formData.theme)?.gradient}`}>
                        {/* Cover Image */}
                        {coverPreview && (
                            <div className="h-32 overflow-hidden">
                                <img
                                    src={coverPreview}
                                    alt="Cover"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        )}

                        <div className="p-6 relative">
                            {/* Logo */}
                            {logoPreview && (
                                <div className="absolute -top-12 left-6">
                                    <div className="w-24 h-24 rounded-xl bg-white p-2 shadow-lg">
                                        <img
                                            src={logoPreview}
                                            alt="Logo"
                                            className="w-full h-full object-contain"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className={logoPreview ? "mt-12" : ""}>
                                <h4 className="text-2xl font-bold text-white">{formData.fullName || 'Nombre Completo'}</h4>
                                <p className="text-white/90">{formData.position || 'Cargo'}</p>
                                <p className="text-white/80 text-sm mt-1">{formData.businessName || 'Nombre del Negocio'}</p>

                                <div className="mt-6 space-y-3">
                                    {formData.email && (
                                        <div className="flex items-center space-x-3 text-white">
                                            <Mail size={18} />
                                            <span className="text-sm">{formData.email}</span>
                                        </div>
                                    )}

                                    {formData.phone && (
                                        <div className="flex items-center space-x-3 text-white">
                                            <Phone size={18} />
                                            <span className="text-sm">{formData.phone}</span>
                                        </div>
                                    )}

                                    {formData.address && (
                                        <div className="flex items-center space-x-3 text-white">
                                            <MapPin size={18} />
                                            <span className="text-sm">{formData.address}</span>
                                        </div>
                                    )}

                                    {formData.website && (
                                        <div className="flex items-center space-x-3 text-white">
                                            <Globe size={18} />
                                            <span className="text-sm">{formData.website}</span>
                                        </div>
                                    )}
                                </div>

                                {formData.bio && (
                                    <div className="mt-4 p-3 bg-white/10 rounded-lg">
                                        <p className="text-white text-sm">{formData.bio}</p>
                                    </div>
                                )}

                                {/* Social Media Icons */}
                                <div className="mt-6 flex flex-wrap gap-3">
                                    {formData.socialMedia.instagram && (
                                        <a href={formData.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-white hover:scale-110 transition-transform">
                                            <Instagram size={20} />
                                        </a>
                                    )}
                                    {formData.socialMedia.facebook && (
                                        <a href={formData.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-white hover:scale-110 transition-transform">
                                            <Facebook size={20} />
                                        </a>
                                    )}
                                    {formData.socialMedia.linkedin && (
                                        <a href={formData.socialMedia.linkedin} target="_blank" rel="noopener noreferrer" className="text-white hover:scale-110 transition-transform">
                                            <Linkedin size={20} />
                                        </a>
                                    )}
                                    {formData.socialMedia.twitter && (
                                        <a href={formData.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-white hover:scale-110 transition-transform">
                                            <Twitter size={20} />
                                        </a>
                                    )}
                                    {formData.socialMedia.youtube && (
                                        <a href={formData.socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="text-white hover:scale-110 transition-transform">
                                            <Youtube size={20} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-4">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setPreviewMode(false)}
                            className="px-6 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-100"
                        >
                            Editar
                        </motion.button>

                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                                setPreviewMode(false);
                                generateCardLink();
                            }}
                            className="px-6 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-full hover:shadow-lg"
                        >
                            Generar Tarjeta
                        </motion.button>
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );

    const renderShareModal = () => (
        <AnimatePresence>
            {showShareModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                    onClick={() => setShowShareModal(false)}
                >
                    <motion.div
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.9 }}
                        className="bg-white rounded-2xl max-w-md w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                ¡Tarjeta Creada!
                            </h3>
                            <p className="text-gray-600 mb-6">
                                Tu tarjeta digital está lista para compartir
                            </p>

                            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                                <div className="flex items-center space-x-2">
                                    <LinkIcon size={20} className="text-gray-500" />
                                    <input
                                        type="text"
                                        value={generatedLink}
                                        readOnly
                                        className="flex-1 bg-transparent border-none focus:ring-0 text-sm"
                                    />
                                    <button
                                        onClick={copyToClipboard}
                                        className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                                    >
                                        {copied ? <Check size={20} className="text-green-500" /> : <Copy size={20} className="text-gray-500" />}
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={shareViaWhatsApp}
                                    className="flex items-center justify-center space-x-2 bg-green-500 text-white p-3 rounded-lg hover:bg-green-600"
                                >
                                    <Send size={20} />
                                    <span>WhatsApp</span>
                                </motion.button>

                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={shareViaEmail}
                                    className="flex items-center justify-center space-x-2 bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600"
                                >
                                    <Mail size={20} />
                                    <span>Email</span>
                                </motion.button>

                                <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => setShowQrModal(true)}
                                    className="flex items-center justify-center space-x-2 bg-purple-500 text-white p-3 rounded-lg hover:bg-purple-600 col-span-2"
                                >
                                    <QrCode size={20} />
                                    <span>Generar QR</span>
                                </motion.button>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={() => setShowShareModal(false)}
                                    className="px-6 py-2 text-gray-600 hover:text-gray-800"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    const renderQrModal = () => (
        <AnimatePresence>
            {showQrModal && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
                    onClick={() => setShowQrModal(false)}
                >
                    <motion.div
                        initial={{ scale: 0.9 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.9 }}
                        className="bg-white rounded-2xl max-w-md w-full p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                            Código QR de tu Tarjeta
                        </h3>

                        <div className="bg-gray-50 p-6 rounded-lg flex justify-center mb-6">
                            <div className="w-48 h-48 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
                                <QrCode size={120} className="text-white" />
                            </div>
                        </div>

                        <p className="text-sm text-gray-600 text-center mb-6">
                            Escanea este código para ver tu tarjeta digital
                        </p>

                        <div className="flex justify-end space-x-4">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={downloadCard}
                                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-full"
                            >
                                <Download size={18} />
                                <span>Descargar</span>
                            </motion.button>

                            <button
                                onClick={() => setShowQrModal(false)}
                                className="px-6 py-2 text-gray-600 hover:text-gray-800"
                            >
                                Cerrar
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return (
        <section id="digital-card" className="min-h-screen bg-white py-12 md:py-20 relative overflow-hidden">
            {/* Stars background like in your Services component */}
            <div className="absolute inset-0">
                {[...Array(50)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1 h-1 bg-secondary rounded-full"
                        style={{
                            left: `${Math.random() * 100}%`,
                            top: `${Math.random() * 100}%`,
                        }}
                        animate={{
                            opacity: [0.2, 1, 0.2],
                            scale: [1, 1.5, 1],
                        }}
                        transition={{
                            duration: Math.random() * 3 + 2,
                            repeat: Infinity,
                            delay: Math.random() * 2,
                        }}
                    />
                ))}
            </div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-12"
                >
                    <div className="flex justify-center mb-4">
                        <Smartphone size={48} className="text-primary" />
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold gradient-text mb-4 drop-shadow-md">
                        Tarjeta de Presentación Digital
                    </h2>
                    <p className="text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
                        Crea tu tarjeta digital interactiva y compártela al instante
                    </p>
                </motion.div>

                {/* Progress Steps */}
                <div className="mb-8">
                    <div className="flex items-center justify-between max-w-md mx-auto">
                        {[1, 2, 3].map((num) => (
                            <div key={num} className="flex items-center">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= num
                                    ? 'bg-gradient-to-r from-primary to-secondary text-white'
                                    : 'bg-gray-200 text-gray-500'
                                    }`}>
                                    {num}
                                </div>
                                {num < 3 && (
                                    <div className={`w-16 h-1 mx-2 ${step > num ? 'bg-gradient-to-r from-primary to-secondary' : 'bg-gray-200'
                                        }`} />
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Form Container */}
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-200">
                    <AnimatePresence mode="wait">
                        {step === 1 && renderStep1()}
                        {step === 2 && renderStep2()}
                        {step === 3 && renderStep3()}
                    </AnimatePresence>
                </div>

                {/* Features */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mt-12 grid md:grid-cols-3 gap-6"
                >
                    <div className="text-center p-6 bg-white rounded-xl border border-gray-200">
                        <Sparkles className="mx-auto text-primary mb-3" size={32} />
                        <h4 className="font-semibold text-gray-900 mb-2">Personalizable</h4>
                        <p className="text-sm text-gray-600">Elige colores y estilo que representen tu marca</p>
                    </div>

                    <div className="text-center p-6 bg-white rounded-xl border border-gray-200">
                        <Share2 className="mx-auto text-primary mb-3" size={32} />
                        <h4 className="font-semibold text-gray-900 mb-2">Compartible</h4>
                        <p className="text-sm text-gray-600">Comparte por WhatsApp, email o código QR</p>
                    </div>

                    <div className="text-center p-6 bg-white rounded-xl border border-gray-200">
                        <Smartphone className="mx-auto text-primary mb-3" size={32} />
                        <h4 className="font-semibold text-gray-900 mb-2">Siempre disponible</h4>
                        <p className="text-sm text-gray-600">Accede desde cualquier dispositivo</p>
                    </div>
                </motion.div>
            </div>

            {/* Modals */}
            <AnimatePresence>
                {previewMode && renderPreview()}
                {showShareModal && renderShareModal()}
                {showQrModal && renderQrModal()}
            </AnimatePresence>
        </section>
    );
};

export default DigitalPresentationCard;